
import React, { useState, useEffect } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { ArticleAuthor } from "@/types/article-types";
import { PlusCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AuthorsTable from "@/components/admin/authors/AuthorsTable";
import AuthorFormDialog from "@/components/admin/authors/AuthorFormDialog";

interface AuthorsTabProps {
  authors: ArticleAuthor[];
  createAuthor: (author: Partial<ArticleAuthor>) => Promise<ArticleAuthor | null>;
  updateAuthor: (id: string, author: Partial<ArticleAuthor>) => Promise<boolean>;
  deleteAuthor: (id: string) => Promise<boolean>;
  uploadMedia: (file: File, path: string) => Promise<string | null>;
}

const AuthorsTab = ({
  authors,
  createAuthor,
  updateAuthor,
  deleteAuthor,
  uploadMedia
}: AuthorsTabProps) => {
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<ArticleAuthor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // When authors data changes (loaded), update loading state
    if (authors !== undefined) {
      setIsLoading(false);
    }
  }, [authors]);

  const handleAddNew = () => {
    setEditingAuthor(null);
    setOpenFormDialog(true);
  };

  const handleEdit = (author: ArticleAuthor) => {
    setEditingAuthor(author);
    setOpenFormDialog(true);
  };

  const handleClose = () => {
    setOpenFormDialog(false);
    setEditingAuthor(null);
  };

  return (
    <TabsContent value="authors" className="space-y-4">
      <div className="flex justify-between items-center bg-corporate-primary-25 p-4 rounded-lg border border-corporate-primary-50">
        <h2 className="text-2xl font-bold text-corporate-primary-100">Author Management</h2>
        <Button onClick={handleAddNew} className="flex items-center gap-2 bg-corporate-primary-75 hover:bg-corporate-primary-100">
          <PlusCircle className="h-4 w-4" />
          Add New Author
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive" className="my-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}
      
      {isLoading ? (
        <div className="text-center p-8 border rounded-lg flex flex-col items-center gap-4 bg-gray-50">
          <Loader2 className="h-8 w-8 animate-spin text-corporate-primary-100" />
          <p className="text-corporate-primary-75">Loading authors...</p>
        </div>
      ) : authors.length === 0 ? (
        <div className="text-center p-8 border rounded-lg bg-gray-50">
          <p className="text-corporate-primary-75 mb-4">No authors found</p>
          <Button onClick={handleAddNew} variant="outline">
            <PlusCircle className="h-4 w-4 mr-2" />
            Create your first author
          </Button>
        </div>
      ) : (
        <AuthorsTable 
          authors={authors || []}
          onEdit={handleEdit}
          onDelete={deleteAuthor}
        />
      )}
      
      <AuthorFormDialog
        open={openFormDialog}
        onClose={handleClose}
        author={editingAuthor}
        onSubmit={editingAuthor ? 
          (data) => updateAuthor(editingAuthor.id, data) : 
          createAuthor}
        uploadMedia={uploadMedia}
      />
    </TabsContent>
  );
};

export default AuthorsTab;
